/**
 * Admin UI for access groups.
 *
 * Admins tick *pages*, never endpoint paths. The mapping from a page to the
 * endpoints it needs lives in the backend catalog, so composing a group here draws
 * on reviewed, tested building blocks — you cannot invent a permission set from
 * this screen. That is deliberate: working out one page's true endpoint set took
 * reading three API modules, and it is not a judgement to re-make in a text field.
 *
 * Server errors are surfaced verbatim rather than replaced with a generic failure.
 * All four guardrails communicate through `detail`, and "You cannot restrict
 * yourself" is only useful if the admin actually reads it.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/common';
import { PAGE_REGISTRY } from '@/navigation/page-registry';
import { useToast } from '@/stores/uiStore';
import { useTeamStore } from '@/stores/teamStore';
import { useAuthStore } from '@/stores/authStore';
import {
  createAccessGroup,
  deleteAccessGroup,
  listAccessGroups,
  setMemberAccessGroup,
  updateAccessGroup,
  type AccessGroup,
} from '@/api/access-groups';
import type { Team, TeamMember } from '@/types/api';

interface AccessGroupsSectionProps {
  team: Team;
}

/** Human label for a page key, falling back to the key when it is not registered. */
function pageLabel(key: string): string {
  const entry = (PAGE_REGISTRY as Record<string, { label?: string } | undefined>)[key];
  return entry?.label ?? key;
}

function errorMessage(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data
    ?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export function AccessGroupsSection({ team }: AccessGroupsSectionProps) {
  const toast = useToast();
  const members = useTeamStore((state) => state.members);
  const setMembers = useTeamStore((state) => state.setMembers);
  const currentUserId = useAuthStore((state) => state.user?.id);

  const [groups, setGroups] = useState<AccessGroup[]>([]);
  const [availablePages, setAvailablePages] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newPages, setNewPages] = useState<string[]>([]);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editPages, setEditPages] = useState<string[]>([]);

  const groupsById = useMemo(
    () => new Map(groups.map((group) => [group.id, group])),
    [groups],
  );

  const load = useCallback(async () => {
    try {
      const data = await listAccessGroups(team.id);
      setGroups(data.groups);
      setAvailablePages(data.available_page_keys);
    } catch (err) {
      toast.error(errorMessage(err, 'Could not load access groups'));
    } finally {
      setLoaded(true);
    }
  }, [team.id, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const togglePage = (list: string[], key: string): string[] =>
    list.includes(key) ? list.filter((k) => k !== key) : [...list, key];

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.error('Give the group a name');
      return;
    }
    setBusy(true);
    try {
      await createAccessGroup(team.id, {
        name: newName.trim(),
        description: newDescription.trim() || null,
        allowed_page_keys: newPages,
      });
      setNewName('');
      setNewDescription('');
      setNewPages([]);
      setShowCreate(false);
      await load();
      toast.success('Access group created');
    } catch (err) {
      toast.error(errorMessage(err, 'Could not create the group'));
    } finally {
      setBusy(false);
    }
  };

  const handleSavePages = async (group: AccessGroup) => {
    setBusy(true);
    try {
      await updateAccessGroup(team.id, group.id, { allowed_page_keys: editPages });
      setEditingId(null);
      await load();
      toast.success(`Updated ${group.name}`);
    } catch (err) {
      toast.error(errorMessage(err, 'Could not update the group'));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (group: AccessGroup) => {
    setBusy(true);
    try {
      await deleteAccessGroup(team.id, group.id);
      await load();
      toast.success(`Deleted ${group.name}`);
    } catch (err) {
      // The server explains *why* — most often that members still use the group,
      // and deleting it would silently give them full access.
      toast.error(errorMessage(err, 'Could not delete the group'));
    } finally {
      setBusy(false);
    }
  };

  const handleAssign = async (member: TeamMember, value: string) => {
    const groupId = value === '' ? null : value;
    setBusy(true);
    try {
      await setMemberAccessGroup(team.id, member.user_id, groupId);
      // The select is controlled by the team store, and `load()` only refreshes
      // groups. Without this the row re-renders from the stale member and snaps
      // back to "Full access" even though the change was saved -- which reads as
      // "it didn't work" when the only thing wrong is what the screen says.
      setMembers(
        members.map((m) =>
          m.user_id === member.user_id ? { ...m, access_group_id: groupId } : m,
        ),
      );
      await load();
      toast.success(
        groupId
          ? `${member.name || member.email} restricted to ${groupsById.get(groupId)?.name ?? 'group'}`
          : `${member.name || member.email} has full access`,
      );
    } catch (err) {
      toast.error(errorMessage(err, 'Could not change access'));
    } finally {
      setBusy(false);
    }
  };

  const pageCheckboxes = (selected: string[], onToggle: (key: string) => void) => (
    <div className="access-groups__pages">
      {availablePages.map((key) => (
        <label key={key} className="form-checkbox-label">
          <input
            type="checkbox"
            checked={selected.includes(key)}
            onChange={() => onToggle(key)}
          />
          {pageLabel(key)}
        </label>
      ))}
    </div>
  );

  return (
    <div className="settings-section">
      <h3 className="section-title">Access Groups</h3>
      <p className="access-groups__hint">
        A group grants a set of pages. Anyone assigned to one can reach only those
        pages, and the limit is enforced by the API rather than hidden in the menu.
      </p>

      {!loaded ? (
        <p className="access-groups__hint">Loading…</p>
      ) : (
        <>
          {groups.length > 0 && (
            <div className="access-groups__list">
              {groups.map((group) => (
                <div key={group.id} className="access-groups__row">
                  <div className="access-groups__row-main">
                    <span className="access-groups__name">{group.name}</span>
                    <span className="access-groups__meta">
                      {group.member_count} member{group.member_count === 1 ? '' : 's'}
                      {' · '}
                      {group.allowed_page_keys.length === 0
                        ? 'no pages'
                        : group.allowed_page_keys.map(pageLabel).join(', ')}
                    </span>
                    {group.description && (
                      <span className="access-groups__meta">{group.description}</span>
                    )}
                  </div>

                  <div className="button-row">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={busy}
                      onClick={() => {
                        setEditingId(editingId === group.id ? null : group.id);
                        setEditPages(group.allowed_page_keys);
                      }}
                    >
                      {editingId === group.id ? 'Cancel' : 'Edit pages'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={busy}
                      onClick={() => void handleDelete(group)}
                    >
                      Delete
                    </Button>
                  </div>

                  {editingId === group.id && (
                    <div className="access-groups__editor">
                      {pageCheckboxes(editPages, (key) =>
                        setEditPages(togglePage(editPages, key)),
                      )}
                      <Button
                        type="button"
                        size="sm"
                        isLoading={busy}
                        onClick={() => void handleSavePages(group)}
                      >
                        Save pages
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {groups.length === 0 && !showCreate && (
            <p className="access-groups__hint">
              No groups yet, so everyone has full access.
            </p>
          )}

          {!showCreate && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowCreate(true)}
            >
              + New Access Group
            </Button>
          )}

          {showCreate && (
            // `active` matters: .invite-section is display:none without it, so the
            // panel would render into the DOM and never appear.
            <div className="invite-section active">
              <h4 className="invite-section__title">New Access Group</h4>
              <div className="form-group">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="CX — OOS Queue only"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="External CX contractors"
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Pages this group can reach</label>
                {pageCheckboxes(newPages, (key) => setNewPages(togglePage(newPages, key)))}
              </div>
              <div className="button-row">
                <Button variant="outline" onClick={() => setShowCreate(false)}>
                  Cancel
                </Button>
                <Button isLoading={busy} onClick={() => void handleCreate()}>
                  Create Group
                </Button>
              </div>
            </div>
          )}

          <h4 className="access-groups__subtitle">Member access</h4>
          <p className="access-groups__hint">
            Your own access is set by another admin — restricting yourself would remove
            this screen.
          </p>
          <div className="access-groups__list">
            {members.map((member: TeamMember) => {
              const isSelf = member.user_id === currentUserId;
              return (
                <div key={member.id} className="access-groups__row">
                  <div className="access-groups__row-main">
                    <span className="access-groups__name">
                      {member.name || member.email}
                    </span>
                    <span className="access-groups__meta">{member.email}</span>
                  </div>
                  <select
                    className="form-input access-groups__select"
                    value={member.access_group_id ?? ''}
                    disabled={busy || isSelf}
                    aria-label={`Access for ${member.name || member.email}`}
                    onChange={(e) => void handleAssign(member, e.target.value)}
                  >
                    {/* Never a blank option. An empty selection reads as "not set"
                        when it actually means this person can reach everything — the
                        remove-restriction control is also the grant-everything
                        control, and it should look like a decision. */}
                    <option value="">Full access</option>
                    {groups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default AccessGroupsSection;
